import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import TextTranslate from "../../../utils/TextTranslate";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { useEffect, useState } from "react";
import { socketInstance } from "../../../socket/socket";
import { resetCallData } from "../../../store/Slices/CallDataSlice";
import {
  markCallJoined,
  playCallEndSound,
  startIncomingRingtone,
  stopIncomingRingtone,
  clearCallSessionKeys,
} from "../../../utils/callSounds";
import { updatePeerData } from "../../../store/Slices/PeerJsSlice";

export default function AcceptVideoCall() {
  const navigate = useNavigate();
  const ViewManager = useAppSelector((state) => state.ViewManager);
  const CallData = useAppSelector((state) => state.CallData);
  const dispatch = useAppDispatch();
  const [soundNeedsGesture, setSoundNeedsGesture] = useState(false);
  const socket = socketInstance();
  const peerData = useAppSelector((state) => state.PeerJsSlice);

  const callSound = () => {
    const el = startIncomingRingtone();
    el.play().catch((error) => {
      console.warn("Incoming call sound blocked until user gesture:", error);
      setSoundNeedsGesture(true);
    });
  };

  const tryPlaySoundFromGesture = () => {
    if (!ViewManager.show_accept_call_modal) return;
    if (!soundNeedsGesture) return;
    const el = startIncomingRingtone();
    el
      .play()
      .then(() => setSoundNeedsGesture(false))
      .catch(() => {});
  };

  const stopSound = () => {
    setSoundNeedsGesture(false);
    stopIncomingRingtone();
  };

  useEffect(() => {
    if (ViewManager.show_missed_call_modal) {
      stopSound();
    }
  }, [ViewManager.show_missed_call_modal]);

  function acceptCall() {
    markCallJoined();
    dispatch(updatePeerData({ call_joined: true }));
    dispatch(updateViewState({ show_accept_call_modal: false }));
    stopSound();
    navigate("/video-call");
  }

  function declineCall() {
    dispatch(updateViewState({ show_accept_call_modal: false }));
    stopSound();
    socket.emit("decline_call", {
      chat_id: peerData.chat_id,
      peer_id: peerData.peer_id,
      call_id: peerData.call_id,
    });
    clearCallSessionKeys();
    dispatch(
      updatePeerData({
        call_id: 0,
        chat_id: "",
        room_id: "",
        peer_id: "",
        call_joined: false,
      }),
    );
    playCallEndSound();
  }

  function closeMissedCallModal() {
    stopSound();
    dispatch(
      updateViewState({
        show_missed_call_modal: false,
        show_accept_call_modal: false,
      }),
    );
    dispatch(resetCallData({}));
  }

  const ringingCallId = CallData?.call?.call_id;
  useEffect(() => {
    if (ViewManager.show_accept_call_modal) {
      stopSound();
      callSound();
    } else {
      stopSound();
    }
    return () => stopSound();
  }, [ViewManager.show_accept_call_modal, ringingCallId]);

  if (!CallData?.chat?.chat_type) {
    return null;
  }

  const isMissedCall = ViewManager.show_missed_call_modal;

  return (
    <>
      <Dialog
        open={ViewManager.show_accept_call_modal || isMissedCall}
        as="div"
        className="relative z-10"
        onClose={() => {}}
      >
        <div className="fixed inset-0 z-10 backdrop-blur-sm">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel
              transition
              onPointerDownCapture={() => {
                if (!isMissedCall) tryPlaySoundFromGesture();
              }}
              className="data-[closed]:transform-[scale(95%)] w-full max-w-lg rounded-2xl bg-modalBg p-6 text-center shadow-[rgba(17,_17,_26,_0.3)_0px_0px_16px] backdrop-blur-md transition-all duration-300 ease-in-out data-[closed]:opacity-0"
            >
              <img
                className="mx-auto my-4 h-14 w-14 rounded-full object-cover"
                src={
                  CallData.chat.chat_type === "group"
                    ? CallData.chat.group_icon
                    : CallData.user.profile_pic
                }
                alt=""
              />
              <DialogTitle as="h3" className="text-xl font-medium">
                <TextTranslate
                  text={
                    isMissedCall
                      ? `You missed a call from ${
                          CallData.chat.chat_type === "group"
                            ? CallData.chat.group_name
                            : CallData.user.full_name
                        }`
                      : `A Request from ${
                          CallData.chat.chat_type === "group"
                            ? CallData.chat.group_name
                            : CallData.user.full_name
                        } to accept a call`
                  }
                />
              </DialogTitle>
              <p className="mt-2 text-sm opacity-70">
                <TextTranslate
                  text={
                    isMissedCall
                      ? "Please try calling back later."
                      : "Accept the call to get started discussion"
                  }
                />
              </p>
              {!isMissedCall && soundNeedsGesture && (
                <p className="mt-3 text-sm font-medium text-amber-600 dark:text-amber-400">
                  <TextTranslate text="Tap this window to enable the ringtone (browser requirement)." />
                </p>
              )}

              <div className="mt-10 flex gap-3">
                {isMissedCall ? (
                  <button
                    onClick={closeMissedCallModal}
                    className="relative h-11 w-full overflow-hidden rounded-lg bg-[#E0E0E0] px-4 py-2 text-base font-medium text-black outline-none lg:px-9 lg:text-lg"
                  >
                    <span className="">Close</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={acceptCall}
                      className="relative h-11 w-full overflow-hidden rounded-lg bg-[#76CC76] px-4 py-2 text-base font-medium text-black outline-none lg:px-9 lg:text-lg"
                    >
                      <span className="">Accept</span>
                    </button>
                    <button
                      onClick={declineCall}
                      className="relative h-11 w-full overflow-hidden rounded-lg bg-[#EA5F5F] px-4 py-2 text-base font-medium text-black outline-none lg:px-9 lg:text-lg"
                    >
                      <span className="">Reject</span>
                    </button>
                  </>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
